import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, message, Spin } from 'antd';
import { createMembershipRule, fetchMembershipProductsBulk, mapFormDataToRulePayload, updateMembershipRule } from '../data';
import { isMonthCondition } from './EligibilityConditions/conditionOperators';
import { getCurrencySymbolForStore } from '../utils/currency';

const SkuDetailTable = ({ skus, storefront }) => {
  const [skuDetails, setSkuDetails] = useState({});
  const [loading, setLoading] = useState(false);
  // Stabilize by content so the effect doesn't re-fire on every render from a new array reference.
  const skusKey = skus.join('|');

  useEffect(() => {
    if (!skus.length || !storefront) return;
    let isMounted = true;
    setLoading(true);
    fetchMembershipProductsBulk({ storeLabel: storefront, skus })
      .then((products) => {
        if (!isMounted) return;
        const bySku = {};
        products.forEach((item) => {
          if (item?.sku) bySku[item.sku] = item;
        });
        setSkuDetails(bySku);
      })
      .catch(() => {
        if (isMounted) setSkuDetails({});
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skusKey, storefront]);

  if (!skus.length) return <p className='step-ro-value'>—</p>;

  return (
    <table className='gift-sku-table' style={{ width: '90%', borderCollapse: 'collapse', marginTop: 16, marginBottom: 16 }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#6b7280' }}>SKU</th>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontSize: 12, color: '#6b7280' }}>Product Name</th>
        </tr>
      </thead>
      <tbody>
        {skus.map((sku) => {
          const detail = skuDetails[sku];
          return (
            <tr key={sku} style={{ borderTop: '1px solid #e5e7eb' }}>
              <td style={{ padding: '6px 8px', fontSize: 13 }}>{sku}</td>
              <td style={{ padding: '6px 8px', fontSize: 13 }}>
                {loading ? <Spin size="small" /> : (detail?.name || '—')}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

const formatCondition = ({ field, operator, value }) => {
  const unit = value && isMonthCondition(field, operator) ? ' months' : '';
  return `${field || '—'} ${operator || '—'} ${value || '—'}${unit}`;
};

export const formatConditionGroups = (groups = []) => {
  if (!Array.isArray(groups)) return [];

  return groups.map((group) => {
    const safeGroup = group || {};
    const conditions = (safeGroup.conditions || []).map(formatCondition);

    const nestedGroups = formatConditionGroups(safeGroup.children || []).map((nestedGroup) => `Group: ${nestedGroup}`);
    const afterConditions = (safeGroup.afterConditions || []).map((row) => `After: ${formatCondition(row)}`);

    const renderedParts = [...conditions, ...nestedGroups, ...afterConditions];
    return `${safeGroup.type || 'And'} (${renderedParts.join('; ') || '—'})`;
  });
};

const StepReviewAndPublish = ({ onBack, formData, readOnly = false, ruleId = null }) => {
  // Toggle between the readable summary and the complete rule payload.
  const [viewMode, setViewMode] = useState('summary');
  // Tracks which action is in flight: 'publish' | 'draft' | null.
  const [submitting, setSubmitting] = useState(null);
  const navigate = useNavigate();

  const submitRule = async (action) => {
    const isDraft = action === 'draft';
    // Required by the create API; Basic Info validates these, but guard against stale state.
    if (!formData.ruleName?.trim() || !formData.storefront || !formData.ruleDescription?.trim()) {
      message.error('Rule Name, Storefront and Rule Description are required.');
      onBack(0);
      return;
    }

    setSubmitting(action);
    try {
      const extraFields = { status: isDraft ? 'draft' : 'active' };
      const res = ruleId
        ? await updateMembershipRule(ruleId, formData, extraFields)
        : await createMembershipRule(formData, extraFields);
      const fallbackMessage = isDraft ? 'Rule saved as draft' : ruleId ? 'Rule updated successfully' : 'Rule created successfully';
      message.success(res?.message || fallbackMessage);
      navigate('/app/membership-rules');
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || (isDraft ? 'Failed to save draft' : ruleId ? 'Failed to update rule' : 'Failed to create rule'));
    } finally {
      setSubmitting(null);
    }
  };

  // Same body the create-rule API receives; status is added by the clicked button.
  const rulePayload = useMemo(() => mapFormDataToRulePayload(formData), [formData]);
  const currencySymbol = getCurrencySymbolForStore(formData.storefront);
  const activeSkus = formData.listType === 'Excluded' ? formData.excludedSkus : formData.includedSkus;

  const summaryRows = useMemo(() => [
    {
      heading: '1 - Basic Info',
      stepIndex: 0,
      rows: [
        { label: 'Rule Name', value: formData.ruleName || '—' },
        { label: 'Storefront', value: formData.storefront || '—' },
        { label: 'Rule Description', value: formData.ruleDescription || '—' },
      ],
    },
    {
      heading: '2 - Benefit & Mechanic',
      stepIndex: 1,
      rows: [
        { label: 'Minimum Cart Value', value: formData.minCartValue ? `${currencySymbol}${formData.minCartValue}` : '—' },
        { label: 'Gift Enabled', value: formData.giftEnabled ? 'Yes' : 'No' },
        {
          label: 'Gift SKUs',
          render: (
            <SkuDetailTable
              skus={Array.isArray(formData.giftSkus) ? formData.giftSkus : [formData.giftSkus].filter(Boolean)}
              storefront={formData.storefront}
            />
          ),
        },
        { label: 'Gifts Per Order', value: formData.giftsPerOrder || '—' },
        { label: 'Discount Enabled', value: formData.discountEnabled ? 'Yes' : 'No' },
        { label: 'Discount Type', value: formData.discountEnabled ? (formData.discountType || '—') : '—' },
        { label: 'Discount Value', value: formData.discountEnabled ? (formData.discountValue || '—') : '—' },
      ],
    },
    {
      heading: '3 - Eligibility Conditions',
      stepIndex: 2,
      rows: [
        { label: 'Rule Type', value: formData.ruleType || '—' },
        { label: 'Member Type', value: formData.memberType || '—' },
        ...(formData.ruleType === 'Lifecycle Triggered'
          ? [{ label: 'Validity Period (days)', value: formData.triggerWindowDays || '—' }]
          : [
              { label: 'Start Date and Time', value: formatDateTime(formData.activeStartDate) || '—' },
              { label: 'End Date and Time', value: formatDateTime(formData.activeEndDate) || 'No end date' },
            ]),
        { label: 'Attribute Conditions', value: formatConditionGroups(formData?.eligibilityGroups).join('; ') || '—' },
        { label: 'Redemption Limit', value: formData.redemptionLimitUnlimited ? 'Unlimited' : formData.redemptionLimit || '—' },
        { label: 'Priority', value: formData.priority || '—' },
      ],
    },
    {
      heading: '4 - Products (Include / Exclude)',
      stepIndex: 3,
      rows: [
        { label: 'List Type', value: formData.listType || '—' },
        { label: 'SKU Source', value: formData.skuSource || '—' },
        {
          label: formData.listType === 'Excluded' ? 'Excluded SKUs' : 'Included SKUs',
          render: (
            <SkuDetailTable
              skus={Array.isArray(formData.includedSkus) ? formData.includedSkus : [formData.includedSkus].filter(Boolean)}
              storefront={formData.storefront}
            />
          ),
        },
      ],
    },
  ], [formData]);

  return (

    <div>
      <div>
        <div className='step-container'>
          <div className='step-content'>
            <div className='step-header'>
              <div className='step-title'>
                Review & Publish
              </div>
            </div>
          </div>
        </div>

        <div className='publishbox'>          
            <div className='publishbox-header'>
              <div className={`publishbox-header-btn-box ${viewMode === 'json' ? 'json-active' : ''}`}>
                <button className={`header-btn summary-btn ${viewMode === 'summary' ? 'active' : ''}`}
                type="button" onClick={() => setViewMode('summary')}>
                Summary
              </button>
              <button className={`header-btn json-btn ${viewMode === 'json' ? 'active' : ''}`} type="button"
                onClick={() => setViewMode('json')}>
                JSON View
              </button>
              </div>
            </div>
         

          {viewMode === 'summary' ? (
            <div className='publishbox-step'>
              {summaryRows.map((section, index) => (
                <div className='publishbox-step-content' key={section.heading}>
                  <div className='publishbox-step-header'>
                    <div className='publishbox-step-heading'>{section.heading}</div>
                    {!readOnly && <button
                      type="button"
                      className='step-edit-btn'
                      onClick={() => onBack(section.stepIndex)}
                    >
                      Edit
                    </button>}
                  </div>
                  {section.rows.map((row) => (
                    <div key={row.label} className='publishbox-step-row'>
                      <div className='publishbox-step-row-content'>
                        <p className='step-row-label'>{row.label}</p>
                        {row.render ?? <p className='step-ro-value'>{row.value}</p>}
                      </div>
                    </div>
                  ))}
                  {/* {index !== summaryRows.length - 1 && <div style={{ height: 8, background: '#ffffff' }} />} */}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, background: '#111827', color: '#e5e7eb', padding: 16, overflowX: 'auto' }}>
              <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(rulePayload, null, 2)}
              </pre>
              {!readOnly && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#9ca3af' }}>
                  "status" is added on submit: "draft" for Save as Draft, "active" for Publish Rule.
                </div>
              )}
            </div>
          )}
        </div>

        {!readOnly && <div className='step-footer mt-32' >
          <Button onClick={onBack} className='btn-s'>
            ← Previous: Products (Include/Exclude)
          </Button>
          <Button className='btn-s' onClick={() => submitRule('draft')} loading={submitting === 'draft'} disabled={Boolean(submitting) && submitting !== 'draft'}>
            Save as Draft
          </Button>
          <Button type="primary" onClick={() => submitRule('publish')} loading={submitting === 'publish'} disabled={Boolean(submitting) && submitting !== 'publish'} className='btn-p'>
            Publish Rule
          </Button>
        </div>}
      </div>
    </div>
  );
};

export default StepReviewAndPublish;
